/**
 * URL Sanitization Utilities
 *
 * Provides secure URL handling to prevent DOM-based XSS vulnerabilities.
 * These utilities ensure that URL components from user-controllable sources
 * (like window.location) are properly validated and sanitized before being
 * used in href attributes or history API calls.
 */

/** Allowed protocols for safe URLs */
const ALLOWED_PROTOCOLS = ['http:', 'https:'];

/**
 * Validates that a URL uses a safe protocol (http or https).
 * Prevents javascript:, data:, vbscript:, and other dangerous protocols.
 *
 * @param url - The URL string to validate
 * @returns true if the URL uses a safe protocol
 */
export function isSafeProtocol(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    return ALLOWED_PROTOCOLS.includes(parsed.protocol);
  } catch {
    return false;
  }
}

/**
 * Sanitizes a hash/fragment identifier for safe use in URLs.
 * Encodes special characters to prevent XSS through fragment manipulation.
 *
 * @param hash - The hash value (without leading #)
 * @returns Safely encoded hash string
 */
export function sanitizeHash(hash: string): string {
  if (!hash) return '';
  // Remove any leading # if present
  const cleanHash = hash.startsWith('#') ? hash.slice(1) : hash;
  // Encode the hash to prevent injection
  return encodeURIComponent(cleanHash);
}

/**
 * Constructs a safe base URL from the current window.location.
 * Parses and reconstructs the URL to ensure no malicious components persist.
 * Only includes origin, pathname, and search params - explicitly excludes hash.
 *
 * @param locationHref - The window.location.href value (defaults to current location)
 * @returns A sanitized base URL string, or empty string if invalid
 */
export function getSafeBaseUrl(locationHref: string = window.location.href): string {
  try {
    const parsed = new URL(locationHref);

    // Validate protocol
    if (!ALLOWED_PROTOCOLS.includes(parsed.protocol)) {
      console.warn('[urlSanitization] Blocked unsafe protocol:', parsed.protocol);
      return '';
    }

    // Reconstruct URL from parsed components to ensure safety
    // Using URL constructor ensures proper encoding of components
    return parsed.origin + parsed.pathname + parsed.search;
  } catch (e) {
    console.warn('[urlSanitization] Failed to parse URL:', e);
    return '';
  }
}

/**
 * Constructs a safe URL with a hash fragment.
 * Validates the base URL protocol and properly encodes the hash.
 *
 * @param baseUrl - The base URL (without hash)
 * @param hash - The hash/fragment to append (without leading #)
 * @returns A safe URL string with the hash appended, or the current pathname if validation fails
 */
export function buildSafeUrlWithHash(baseUrl: string, hash: string): string {
  if (!baseUrl) {
    // Fallback to current pathname
    return window.location.pathname + '#' + sanitizeHash(hash);
  }

  if (!isSafeProtocol(baseUrl)) {
    console.warn('[urlSanitization] Blocked unsafe base URL protocol');
    return window.location.pathname + '#' + sanitizeHash(hash);
  }

  return baseUrl + '#' + sanitizeHash(hash);
}

/**
 * Sanitizes a pathname to prevent protocol-relative or javascript: URL injection.
 * More restrictive than a simple character filter - validates the structure.
 *
 * @param pathname - The pathname to sanitize
 * @returns A safe pathname string
 */
export function sanitizePathname(pathname: string): string {
  if (!pathname) return '/';

  // Block protocol-relative URLs (//example.com) and javascript: schemes
  if (/^\/\/|^javascript:/i.test(pathname)) {
    console.warn('[urlSanitization] Blocked dangerous pathname pattern');
    return '/';
  }

  // Ensure pathname starts with /
  if (!pathname.startsWith('/')) {
    return '/' + pathname;
  }

  return pathname;
}

/**
 * Constructs a safe href from pathname, search, and hash components.
 * Use this when building href attributes from location components.
 *
 * @param pathname - The URL pathname
 * @param search - The URL search/query string (with or without leading ?)
 * @param hash - The URL hash (without leading #)
 * @returns A safe href string
 */
export function buildSafeHref(pathname: string, search: string, hash: string): string {
  const safePath = sanitizePathname(pathname);
  const safeSearch = search.startsWith('?') ? search : search ? '?' + search : '';
  const safeHash = hash ? '#' + sanitizeHash(hash) : '';

  return safePath + safeSearch + safeHash;
}
